import { ComponentFactoryResolver, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isEmpty } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SideBarConfig, SideBarService } from '../common/service/side-bar.service';
import { MenuButton } from '../shared/model/menu-button';

@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss']
})
export class SideBarComponent implements OnInit, OnDestroy {

  private footerCmp: ViewContainerRef;

  @ViewChild('footerContainer', { read: ViewContainerRef }) set footerContainer(container: ViewContainerRef) {
    this.footerCmp = container;
    if (this.isVisible) {
      this.renderFooterComponents();
    }
  };

  private headerCmp: ViewContainerRef;

  @ViewChild('headerContainer', { read: ViewContainerRef }) set headerContainer(container: ViewContainerRef) {
    this.headerCmp = container;
    if (this.isVisible) {
      this.renderHeaderComponents();
    }
  };

  private unsubscribe$ = new Subject<void>();

  isVisible = false;
  active: MenuButton;

  get menuButtons(): MenuButton[] {
    return this.config?.menuButtons?.filter(b => b.condition);
  }

  private config: SideBarConfig;

  constructor(
    private sideBarService: SideBarService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private factoryResolver: ComponentFactoryResolver
  ) { }

  ngOnInit(): void {
    this.sideBarService.dataSub$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(({ isVisible, activeButton, config }) => {
      const wasVisible = this.isVisible;
      const configChanged = config !== this.config;
      this.isVisible = isVisible;
      this.active = activeButton;
      this.config = config;
      if (isVisible && (!wasVisible || configChanged)) {
        if (this.headerCmp) this.renderHeaderComponents();
        if (this.footerCmp) this.renderFooterComponents();
      }
    });
  }

  handleClick(button: MenuButton): void {
    // Gdy widok jest już aktywny, kliknięcie cykluje stan widoku (np. Odjazdy: dzienny <-> wolny)
    // zamiast ponownej nawigacji. Poza widokiem - zwykła nawigacja.
    if (button == this.active && button.onCycle != null) {
      button.onCycle();
      return;
    }
    this.executeClick(button);
  }

  private executeClick(button: MenuButton): void {
    if (!isEmpty(button.routerLink)) {
      this.router.navigate([button.routerLink], { relativeTo: this.activatedRoute, queryParams: button.queryParams })
        .catch(err => console.error(err));
    } else if (!isEmpty(button.externalLink)) {
      window.open(button.externalLink, '_blank');
    } else if (button.onClick != null) {
      button.onClick();
    } else {
      console.error('none action for button');
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private injectComponentsIntoContainer(targetContainer: ViewContainerRef, componentsToInject: { component: Type<any>; inputs?: Record<string, any> }[]): void {
    if (!targetContainer || !componentsToInject) return;

    requestAnimationFrame(() => {
      targetContainer.clear();
      componentsToInject.forEach(componentCfg => {
        const factory = this.factoryResolver.resolveComponentFactory(componentCfg.component);
        const ref = targetContainer.createComponent(
          factory, undefined, this.config.injectionConfig.injector
        );
        Object.entries(componentCfg.inputs ?? {}).forEach(([key, value]) => {
          ref.instance[key] = value;
        });
      });
    });
  }

  private renderHeaderComponents(): void {
    this.injectComponentsIntoContainer(this.headerCmp, this.config.injectionConfig.headerComponents);
  }

  private renderFooterComponents(): void {
    this.injectComponentsIntoContainer(this.footerCmp, this.config.injectionConfig.footerComponents);
  }

}
